/**
 * New email — `docs/activity.md` §9.4, Board 11 D (620).
 *
 * *To* (the contact and the address) · *Cc* (free addresses) and *From* —
 * *Tom Reyes · your Outlook* when it is connected, *Tom Reyes via <company>*
 * otherwise, changeable when there is a choice (§7.7) · *Bcc*, under them
 * once asked for · *Subject* ·
 * **Attached to** · *Message* with the camera, the paperclip and **From the
 * job** · the attachments. Sent with `POST /conversations`.
 *
 * **Cc and Bcc take several addresses, and people in the company** (Aaron,
 * Oct 26, `address-box.tsx`): somebody picked gets the email at their own
 * address and has the conversation in their Inbox.
 */
import { useEffect, useState } from 'react'
import { recordWords } from '@rowted/shared'
import { activityRequest } from '../api.js'
import { Face } from '../faces.js'
import { AttachedTo, CONVERSATION_KINDS, cameWithWords, suggestFor, withChips, type AttachChip } from './attach.js'
import { openedFromWords, problemOf, refsOf, useContact, usePrefillChips, useSetup, type NewPrefill } from './common.js'
import { ContactField } from './contact-field.js'
import { FileChips, FileTools, FromTheJob, useFiles } from './files.js'
import { Field, FieldLabel, Gap, MenuField, NewShell, Pair, TextBox, WordsBox } from './kit.js'
import { AddressBox, badCopyWords, copiesPayload, type Copy } from './address-box.js'
import { SignatureUnder } from '../inbox/signature.js'
import { StoppedWarning, addressesOf, useStoppedAddresses } from '../inbox/stopped.js'
import { Glyph } from '../glyphs.js'

export function EmailSheet({
  prefill, phone, onClose, onMade,
}: { prefill: NewPrefill; phone: boolean; onClose: () => void; onMade: () => void }) {
  const setup = useSetup()
  const [contactId, setContactId] = useState<string | null>(prefill.contactId ?? prefill.contact?.id ?? null)
  const contact = useContact(contactId)
  const [chips, setChips] = usePrefillChips(prefill)
  const [picked, setPicked] = useState<{ chip: AttachChip; brought: AttachChip[] } | null>(null)
  const [cc, setCc] = useState<Copy[]>([])
  const [bcc, setBcc] = useState<Copy[]>([])
  const [bccOpen, setBccOpen] = useState(false)
  const [subject, setSubject] = useState(prefill.subject ?? '')
  const [body, setBody] = useState(prefill.body ?? '')
  const files = useFiles()
  const [via, setVia] = useState<'outlook' | 'rowted' | null>(null)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const fromThread = Boolean(prefill.fromConversationId)
  const hint = cameWithWords(picked) ?? openedFromWords({ name: fromThread ? contact?.name ?? null : null, kind: 'thread', required: true, phone })
  const firstJob = chips.find((chip) => chip.type === 'job') ?? null
  const replyTo = firstJob ? firstJob.label : chips[0] ? recordWords(chips[0]) : null

  const name = setup?.me.name ?? ''
  const current = via ?? setup?.emailVia ?? 'rowted'
  /* Postmark has stopped sending to one of them (Aaron, Oct 26) — a warning, never a refusal. */
  const stopped = useStoppedAddresses(addressesOf(contact?.email, cc, bcc), current === 'rowted')
  const fromOptions = [
    ...(setup?.outlookAddress ? [{ value: 'outlook' as const, label: `${name} · your Outlook`, sub: setup.outlookAddress }] : []),
    { value: 'rowted' as const, label: setup?.rowtedFrom ?? name },
  ]

  const pickContact = async (id: string) => {
    setContactId(id)
    if (chips.some((one) => one.type === 'contact' && one.id === id)) return
    const label = await activityRequest<{ name: string }>(`/activity/compose?contactId=${id}`).then((one) => one.name).catch(() => '')
    const chip: AttachChip = { type: 'contact', id, label, auto: false }
    const brought = (await suggestFor('contact', id)).filter((one) => !chips.some((had) => had.type === one.type && had.id === one.id))
    setChips(withChips([...chips, chip], brought))
    setPicked({ chip, brought })
  }

  const changeVia = async (next: 'outlook' | 'rowted') => {
    setVia(next)
    try { await activityRequest('/account/mail', { method: 'PATCH', body: { sendFrom: next } }) } catch { /* the next send says so */ }
  }

  /* Opened with only a contact (a stop card's *Text*, the Inbox's *New text*): they bring their records (§9.2). */
  useEffect(() => {
    if (prefill.contactId && (prefill.links ?? []).length === 0 && !prefill.fromConversationId) void pickContact(prefill.contactId)
  }, [])

  const send = async () => {
    if (!contact) { setProblem('Pick who it’s to.'); return }
    if (!contact.email) { setProblem(`No email address for ${contact.firstName}`); return }
    if (!subject.trim()) { setProblem('Give it a subject.'); return }
    if (chips.length === 0) { setProblem('Attach it to a contact, property or job.'); return }
    const bad = badCopyWords(cc, bcc)
    if (bad) { setProblem(bad); return }
    setBusy(true)
    setProblem(null)
    try {
      await activityRequest('/conversations', {
        method: 'POST',
        body: {
          contactId: contact.id, channel: 'email', subject: subject.trim(), body: body.trim(),
          ...copiesPayload(cc, bcc),
          fileIds: files.files.map((file) => file.id), links: refsOf(chips),
          ...(prefill.origin ? { origin: prefill.origin } : {}),
        },
      })
      onMade()
      onClose()
    } catch (cause) {
      setProblem(problemOf(cause))
    } finally {
      setBusy(false)
    }
  }

  return (
    <NewShell title="New email" sub={setup?.sub ?? ''} look="email" width={620} phone={phone}
      note={replyTo ? `Replies come back to your Inbox and to ${replyTo}` : 'Replies come back to your Inbox'}
      primary="Send" phoneAction="Send" onPrimary={() => void send()} busy={busy || files.busy}
      dirty={subject.trim().length > 0 || body.trim().length > 0 || files.files.length > 0 || cc.length > 0 || bcc.length > 0}
      onClose={onClose}
      problem={problem ?? files.problem}>
      <Field label="To" required phone={phone}>
        <ContactField label="To" tall phone={phone} contact={contact} onPick={(id) => void pickContact(id)}
          detail={contact?.email ?? (contact ? 'no email address' : null)} />
      </Field>
      <StoppedWarning stopped={stopped} to={contact?.email} className="mt-2" />
      <Gap size={10} />
      <Pair phone={phone}>
        <div className="min-w-0">
          <div className="flex items-start">
            <div className="flex-1"><FieldLabel label="Cc" /></div>
            {!bccOpen && !phone && (
              <button type="button" onClick={() => setBccOpen(true)}
                className="text-[11.5px] font-semibold text-accent hover:text-accent-dark">
                Bcc
              </button>
            )}
          </div>
          <AddressBox label="Cc" phone={phone} value={cc} onChange={setCc} taken={bcc} />
        </div>
        <Field label="From" phone={phone}>
          <MenuField label="From" phone={phone} value={current} options={fromOptions}
            onChange={(next) => void changeVia(next)}
            display={(
              <>
                {setup && <Face person={{ id: setup.me.id, name, initials: setup.me.initials, colour: setup.me.colour }} size={20} />}
                <span className="truncate">{fromOptions.find((one) => one.value === current)?.label ?? setup?.emailFrom ?? ''}</span>
              </>
            )} />
        </Field>
      </Pair>
      {/* A phone's way to it is a whole row — a word in the label is not something a thumb can hit. */}
      {!bccOpen && phone && (
        <button type="button" onClick={() => setBccOpen(true)}
          className="mt-1 inline-flex h-11 items-center gap-1 text-[14px] font-semibold text-accent">
          <Glyph name="plus" size={15} />Bcc
        </button>
      )}
      {bccOpen && (
        <>
          <Gap size={10} />
          <Field label="Bcc" phone={phone}>
            <AddressBox label="Bcc" phone={phone} value={bcc} onChange={setBcc} taken={cc} autoFocus />
          </Field>
        </>
      )}
      <Gap size={10} />
      <Field label="Subject" required phone={phone}>
        <TextBox label="Subject" value={subject} onChange={setSubject} phone={phone} bold />
      </Field>
      <Gap size={10} />
      <AttachedTo chips={chips} required phone={phone} hint={hint} id="new-email-attached" kinds={CONVERSATION_KINDS}
        onChange={(next, pick) => { setChips(next); setPicked(pick ?? null) }} />
      <Gap size={10} />
      <Field label="Message" phone={phone}>
        <WordsBox label="Message" value={body} onChange={setBody} minHeight={70} phone={phone}
          tools={(
            <>
              <FileTools onFiles={(list) => void files.add(list)} />
              <FromTheJob jobId={firstJob?.id ?? null} onPick={files.addExisting} />
              <span className="flex-1" />
            </>
          )} />
      </Field>
      {/* What the email will end with (Aaron, Oct 24, note 2.7) — added as it leaves. */}
      <SignatureUnder className="mt-2" />
      <FileChips files={files.files} onRemove={files.remove} />
    </NewShell>
  )
}
